import { useGetStationDisplayName } from "@/entities/station";
import { AangevraagdeVestiging, useGetAangevraagdeVestigingen, VestigingStatus } from "@/entities/vestiging";
import { DossierSummaryCard, DossierSummaryCardSkeleton } from "@/features/dossier-details";
import { Dot, DotVariants, EmptyState, ErrorDisplay, Icon, IconName, Separated } from "@/shared/components";
import { convertToKebabCase } from "@/shared/utils";
import { getListPreview } from "@/shared/utils/list-preview";

interface FaciliteitenSummaryProps {
    dossierNummer: string;
}

export function FaciliteitenSummary({ dossierNummer }: Readonly<FaciliteitenSummaryProps>) {
    const { data: faciliteiten, isPending, error } = useGetAangevraagdeVestigingen(dossierNummer);

    if (isPending) {
        return <DossierSummaryCardSkeleton section="Faciliteiten" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van faciliteiten" />;
    }

    const hasContent = faciliteiten?.length > 0;
    const actieveVestigingen = faciliteiten.filter((faciliteit) => faciliteit.status === VestigingStatus.ACTIEF).length;
    const status = actieveVestigingen > 0 ? `${actieveVestigingen} actieve vestiging${actieveVestigingen === 1 ? "" : "en"}` : "";

    return (
        <DossierSummaryCard section="Faciliteiten" status={status}>
            {hasContent ? (
                <FaciliteitenSummaryContent faciliteiten={faciliteiten} />
            ) : (
                <EmptyState className="pr-3 pl-4">Geen faciliteiten aangevraagd</EmptyState>
            )}
        </DossierSummaryCard>
    );
}

type FaciliteitenSummaryContentProps = {
    faciliteiten: AangevraagdeVestiging[];
};

function FaciliteitenSummaryContent({ faciliteiten }: Readonly<FaciliteitenSummaryContentProps>) {
    const getStationDisplayName = useGetStationDisplayName();
    const aangevraagdeVestigingenByStation = AangevraagdeVestiging.groupByStation(faciliteiten);

    const items = Object.entries(aangevraagdeVestigingenByStation)
        .map(([stationCode, vestigingen]) => ({
            stationName: getStationDisplayName(stationCode),
            counts: getStationCounts(vestigingen ?? []),
            dotVariant: getStationDotVariant(vestigingen ?? []),
        }))
        .sort((a, b) => (a.stationName ?? "").localeCompare(b.stationName ?? ""));

    const { previewItems, remainder } = getListPreview(items, 5);

    return (
        <div className="grid gap-1">
            <ol className="text-content-default flex flex-col">
                <Separated>
                    {previewItems.map((item) => (
                        <li
                            key={item.stationName}
                            className="flex items-center justify-between px-4 py-2"
                            data-testid={`faciliteiten-item-${convertToKebabCase(item.stationName)}`}
                        >
                            <span className="text-content-emphasis text-m font-medium">{item.stationName}</span>
                            <div className="flex items-center gap-4">
                                <FaciliteitenAggregate
                                    count={item.counts.inBehandeling}
                                    variant="in behandeling"
                                    testId="faciliteiten-in-behandeling"
                                />
                                <FaciliteitenAggregate count={item.counts.actief} variant="actief" testId="faciliteiten-actief" />
                                <FaciliteitenAggregate count={item.counts.gestopt} variant="gestopt" testId="faciliteiten-gestopt" />
                                <Dot variant={item.dotVariant} data-testid="faciliteiten-status-dot" />
                            </div>
                        </li>
                    ))}
                </Separated>
            </ol>
            {remainder > 0 && <p className="text-content-subtle text-s border-t px-4 pt-2">Nog {remainder} stations</p>}
        </div>
    );
}

type FaciliteitenAggregateProps = {
    count: number;
    variant: "in behandeling" | "actief" | "gestopt";
    testId?: string;
};

function FaciliteitenAggregate({ count, variant, testId }: Readonly<FaciliteitenAggregateProps>) {
    const iconMap: Record<FaciliteitenAggregateProps["variant"], IconName> = {
        actief: "check-circle",
        gestopt: "minus-circle",
        "in behandeling": "clock",
    };

    return (
        <span className="text-m flex items-center gap-2" data-testid={testId}>
            <Icon name={iconMap[variant]} className="text-content-emphasis" />
            {count} {variant}
        </span>
    );
}

type AangevraagdeVestigingStationCounts = {
    inBehandeling: number;
    actief: number;
    gestopt: number;
};

function getStationCounts(aangevraagdeVestigingen: AangevraagdeVestiging[]): AangevraagdeVestigingStationCounts {
    const countByStatus = Object.fromEntries(
        Object.entries(Object.groupBy(aangevraagdeVestigingen, (av) => av.status)).map(([status, vestigingen]) => [
            status,
            vestigingen.length,
        ])
    ) as Record<VestigingStatus, number>;

    const count = (status: VestigingStatus) => countByStatus[status] ?? 0;

    return {
        inBehandeling: count(VestigingStatus.BESTELD) + count(VestigingStatus.STOP_AANGEVRAAGD),
        actief: count(VestigingStatus.ACTIEF),
        gestopt: count(VestigingStatus.GESTOPT) + count(VestigingStatus.GAAT_NIET_LEVEREN),
    };
}

function getStationDotVariant(aangevraagdeVestigingen: AangevraagdeVestiging[]): DotVariants["variant"] {
    const { inBehandeling, actief } = getStationCounts(aangevraagdeVestigingen);

    if (inBehandeling > 0) {
        // Edge case: everything is still in behandeling, no response yet
        return inBehandeling === aangevraagdeVestigingen.length ? "infoOpen" : "info";
    }

    if (actief > 0) {
        return "succes";
    }

    return "neutral";
}
